/**
 * Pure repayment maths mirroring the Ideal4Finance calculator widget.
 * Rates are representative only; the customer's actual APR/term depends on
 * their status per the IAR disclaimer.
 */
export const REPRESENTATIVE_APR = 11.9; // representative APR (%) used for display
export const I4F_APR_OPTIONS = [0, 11.9] as const;

export interface CalcInputs {
  amount: number;
  deposit?: number;
  termMonths: number;
  aprPercent?: number;
}

export interface CalcResult {
  amount: number;
  deposit: number;
  amountOfCredit: number;
  termMonths: number;
  aprPercent: number;
  monthly: number;
  total: number;
  interest: number;
  financeType: "Interest Free" | "Interest Bearing";
}

export function calculateRepayment({
  amount,
  deposit = 0,
  termMonths,
  aprPercent = REPRESENTATIVE_APR,
}: CalcInputs): CalcResult {
  const price = Math.max(0, amount);
  const dep = Math.max(0, Math.min(deposit, price));
  const P = Math.max(0, price - dep);
  const n = Math.max(1, Math.floor(termMonths));
  const monthly =
    aprPercent === 0
      ? P / n
      : (P * (aprPercent / 100 / 12)) /
        (1 - Math.pow(1 + aprPercent / 100 / 12, -n));
  const monthlySafe = Number.isFinite(monthly) ? monthly : 0;
  const total = monthlySafe * n;
  const interest = total - P;
  return {
    amount: round2(price),
    deposit: round2(dep),
    amountOfCredit: round2(P),
    termMonths: n,
    aprPercent,
    monthly: round2(monthlySafe),
    total: round2(total),
    interest: round2(interest),
    financeType: aprPercent === 0 ? "Interest Free" : "Interest Bearing",
  };
}

function round2(x: number): number {
  return Math.round(x * 100) / 100;
}

export function formatGBP(value: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 2,
  }).format(value);
}
